import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import type { AutocompleteRenderGetTagProps } from "@mui/material/Autocomplete";
import { apiService } from "../../api/services/apiService";
import { useLinks } from "../../hooks/useLinks";
import { useDocuments } from "../../hooks/useDocuments";
import { useCategories } from "../../hooks/useCategories";
import { useFaqs } from "../../hooks/useFaqs";
import { useDeveloperGuides } from "../../hooks/useDeveloperGuides";
import { EntityStatus } from "../../types";
import { formatApiDisplayName } from "../../utils/apiDisplayName";

export interface ProductRelationOption {
  uuid: string;
  name: string;
  status?: string;
}

export interface ProductRelationsSelection {
  apis: ProductRelationOption[];
  categories: ProductRelationOption[];
  documents: ProductRelationOption[];
  faqs: ProductRelationOption[];
  links: ProductRelationOption[];
  developerGuides: ProductRelationOption[];
}

interface ProductRelationsPickerProps {
  value: ProductRelationsSelection;
  onChange: (value: ProductRelationsSelection) => void;
  disabled?: boolean;
}

const getUnselectedOptions = (
  all: ProductRelationOption[],
  selected: ProductRelationOption[],
) => {
  const selectedIds = new Set(selected.map((o) => o.uuid));
  return all.filter(
    (o) =>
      !selectedIds.has(o.uuid) &&
      (o.status == null || o.status === EntityStatus.ACTIVE),
  );
};

const createRenderGreenChips =
  (formatLabel?: (option: ProductRelationOption) => string) =>
  (
    value: ProductRelationOption[],
    getTagProps: AutocompleteRenderGetTagProps,
  ) =>
    value.map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={option.uuid}
        label={formatLabel ? formatLabel(option) : option.name}
        sx={{ bgcolor: "#90e790", fontWeight: 600 }}
      />
    ));

const renderGreenChips = createRenderGreenChips();
const renderApiGreenChips = createRenderGreenChips((o) =>
  formatApiDisplayName(o.name),
);

const ProductRelationsPicker: React.FC<ProductRelationsPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [apis, setApis] = useState<ProductRelationOption[]>([]);
  const [loadingApis, setLoadingApis] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  const { links, loading: loadingLinks } = useLinks();
  const { documents, loading: loadingDocuments } = useDocuments();
  const { categories, loading: loadingCategories } = useCategories();
  const { faqs, loading: loadingFaqs } = useFaqs();
  const { developerGuides, loading: loadingGuides } = useDeveloperGuides();

  useEffect(() => {
    let cancelled = false;
    setLoadingApis(true);
    setApiError(null);
    apiService
      .getApis()
      .then((data) => {
        if (!cancelled) {
          setApis(
            data.map((a) => ({ uuid: a.uuid, name: a.name, status: a.status })),
          );
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setApiError(
            err instanceof Error ? err.message : "Failed to fetch APIs",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingApis(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryOptions: ProductRelationOption[] = categories.map((c) => ({
    uuid: c.uuid,
    name: c.name,
    status: c.status,
  }));
  const faqOptions: ProductRelationOption[] = faqs.map((f) => ({
    uuid: f.uuid,
    name: f.name,
    status: f.status,
  }));
  const linkOptions: ProductRelationOption[] = links.map((l) => ({
    uuid: l.uuid,
    name: l.name,
    status: l.status,
  }));
  const documentOptions: ProductRelationOption[] = documents.map((d) => ({
    uuid: d.uuid,
    name: d.name,
    status: d.status,
  }));
  const guideOptions: ProductRelationOption[] = developerGuides.map((g) => ({
    uuid: g.uuid,
    name: g.name,
    status: g.status,
  }));

  const patch = (partial: Partial<ProductRelationsSelection>) =>
    onChange({ ...value, ...partial });

  return (
    <>
      {!loadingCategories && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(categoryOptions, value.categories)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.categories}
          onChange={(_, v) => patch({ categories: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField {...p} label="Categories" fullWidth margin="normal" />
          )}
        />
      )}

      <Autocomplete
        multiple
        disabled={disabled}
        options={getUnselectedOptions(apis, value.apis)}
        getOptionLabel={(o) => formatApiDisplayName(o.name)}
        isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
        value={value.apis}
        onChange={(_, v) => patch({ apis: v })}
        loading={loadingApis}
        renderTags={renderApiGreenChips}
        renderInput={(p) => (
          <TextField
            {...p}
            label="APIs"
            fullWidth
            margin="normal"
            error={!!apiError}
            helperText={apiError}
            InputProps={{
              ...p.InputProps,
              endAdornment: (
                <>
                  {loadingApis && <CircularProgress size={18} />}
                  {p.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {!loadingFaqs && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(faqOptions, value.faqs)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.faqs}
          onChange={(_, v) => patch({ faqs: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField {...p} label="FAQs" fullWidth margin="normal" />
          )}
        />
      )}

      {!loadingLinks && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(linkOptions, value.links)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.links}
          onChange={(_, v) => patch({ links: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField {...p} label="Links" fullWidth margin="normal" />
          )}
        />
      )}

      {!loadingDocuments && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(documentOptions, value.documents)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.documents}
          onChange={(_, v) => patch({ documents: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField {...p} label="Documents" fullWidth margin="normal" />
          )}
        />
      )}

      {!loadingGuides && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(guideOptions, value.developerGuides)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.developerGuides}
          onChange={(_, v) => patch({ developerGuides: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField
              {...p}
              label="Developer Guides"
              fullWidth
              margin="normal"
            />
          )}
        />
      )}
    </>
  );
};

export default ProductRelationsPicker;

export function productToRelationsSelection(
  product: {
    apis?: { uuid: string; name: string; status?: string }[];
    categories?: { uuid: string; name: string; status?: string }[];
    documents?: { uuid: string; name: string; status?: string }[];
    faqs?: { uuid: string; name: string; status?: string }[];
    links?: { uuid: string; name: string; status?: string }[];
    developerGuides?: { uuid: string; name: string; status?: string }[];
  },
): ProductRelationsSelection {
  const map = (items?: { uuid: string; name: string; status?: string }[]) =>
    (items ?? []).map((item) => ({
      uuid: item.uuid,
      name: item.name,
      status: item.status,
    }));

  return {
    apis: map(product.apis),
    categories: map(product.categories),
    documents: map(product.documents),
    faqs: map(product.faqs),
    links: map(product.links),
    developerGuides: map(product.developerGuides),
  };
}

export function relationsSelectionToUuids(selection: ProductRelationsSelection) {
  return {
    apiUuids: selection.apis.map((a) => a.uuid),
    categoryUuids: selection.categories.map((c) => c.uuid),
    documentUuids: selection.documents.map((d) => d.uuid),
    faqUuids: selection.faqs.map((f) => f.uuid),
    linkUuids: selection.links.map((l) => l.uuid),
    developerGuideUuids: selection.developerGuides.map((g) => g.uuid),
  };
}
